import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, Spinner } from "@/components/Common";
import { api, enc, errMsg } from "@/lib/api";
import { num, pct, useAdminGet, usd, when } from "@/lib/admin";
import { cn } from "@/lib/utils";

const METRICS = [
  ["new_registrations", "New registrations"], ["resume_uploads", "Resume uploads"], ["elite_trials", "New Elite trials"],
  ["pro_subscriptions", "New Pro subs"], ["elite_subscriptions", "New Elite subs"], ["revenue", "Revenue", usd],
  ["trial_conversions", "Trial conversions"], ["cancellations", "Cancellations"], ["ai_cost", "AI expenses", usd],
];

const Metric = ({ id, k, fmt = num, v, delta }) => (
  <div className="rounded-lg border border-[#27272A] bg-[#09090B]/60 p-3" data-testid={`briefing-${id}`}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
    <div className="mt-0.5 flex items-baseline gap-1.5"><span className="font-display text-xl text-zinc-50">{fmt(v)}</span>
      {delta != null && delta !== 0 && <span className={cn("text-[10px] font-semibold", (delta > 0) !== ["cancellations", "ai_cost"].includes(id) ? "text-emerald-400" : "text-red-400")}>{delta > 0 ? "+" : ""}{fmt(delta)}</span>}</div>
  </div>
);

const Campaigns = ({ rows }) => (
  <div data-testid="briefing-campaigns">
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">Marketing campaigns</div>
    {rows.length ? rows.map((c) => (
      <div key={c.id || c.name} className="flex justify-between gap-3 border-b border-[#27272A] py-1.5 text-xs last:border-0">
        <span className="truncate text-zinc-300">{c.name}</span>
        <span className="shrink-0 font-mono text-zinc-400">{num(c.sent)} sent · {pct(c.open_rate)} open · {pct(c.click_rate)} click · {num(c.conversions)} conv</span>
      </div>
    )) : <p className="text-xs text-zinc-500">No campaigns sent yesterday.</p>}
  </div>
);

const Leads = ({ rows }) => (
  <div data-testid="briefing-leads">
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">Leads requiring attention</div>
    {rows.length ? rows.map((l) => (
      <Link key={l.key} to={`/admin/cc/prospects/${enc(l.key)}`} className="flex justify-between gap-3 rounded-md px-1 py-1.5 text-xs hover:bg-[#18181B]" data-testid={`briefing-lead-${l.key}`}>
        <span className="truncate text-zinc-200">{l.name || l.email}</span><span className="shrink-0 text-zinc-500">{l.reason}</span>
      </Link>
    )) : <p className="text-xs text-zinc-500">Nothing urgent.</p>}
  </div>
);

export const DailyBriefing = () => {
  const q = useAdminGet("/admin/cc/briefing");
  const [busy, setBusy] = useState(false);
  const regen = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/cc/briefing/regenerate", {}); q.setData(data); toast.success("Briefing refreshed"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (q.loading && !q.data) return <Card className="mb-8"><Spinner className="text-[#D4AF37]" /></Card>;
  if (q.missing || q.error) return (
    <Card className="mb-8 border-dashed border-[#D4AF37]/30 text-sm text-zinc-400" data-testid="briefing-pending"><Sun className="mr-2 inline h-4 w-4 text-[#D4AF37]" />Your daily business briefing appears here once the briefing route is live on your backend.</Card>
  );
  const d = q.data || {}, m = d.metrics || {}, dl = d.deltas || {};
  return (
    <Card className="mb-8 border-[#D4AF37]/40 bg-gradient-to-br from-[#1d170a] to-[#18181B] p-5 sm:p-6" data-testid="daily-briefing">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">RPOPRO · Daily business briefing</div>
          <div className="mt-1 font-display text-xl text-zinc-50" data-testid="briefing-date">{d.date ? new Date(`${d.date}T12:00:00`).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" }) : "Today"}</div>
          {d.generated_at && <div className="text-[11px] text-zinc-500">Generated {when(d.generated_at)} · covers yesterday</div>}
        </div>
        <Button variant="outline" size="sm" onClick={regen} disabled={busy} className="rounded-full" data-testid="briefing-regenerate">{busy ? <Spinner /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}Refresh</Button>
      </div>
      {d.summary && <p className="mb-4 max-w-3xl text-sm text-zinc-300" data-testid="briefing-summary">{d.summary}</p>}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9">{METRICS.map(([id, k, fmt]) => <Metric key={id} id={id} k={k} fmt={fmt} v={m[id]} delta={dl[id]} />)}</div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2"><Campaigns rows={d.campaigns || []} /><Leads rows={d.leads_attention || []} /></div>
      <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <div className="rounded-xl border border-[#D4AF37]/40 bg-[#33290D]/40 p-4" data-testid="briefing-opportunity">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#F3C649]">Biggest growth opportunity</div>
          <p className="mt-1.5 text-sm text-zinc-100">{d.biggest_opportunity || "—"}</p>
        </div>
        <div className="rounded-xl border border-[#27272A] bg-[#09090B]/60 p-4" data-testid="briefing-actions">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">Three recommended actions for today</div>
          <ol className="mt-2 space-y-1.5">{(d.actions || []).slice(0, 3).map((a, i) => <li key={i} className="flex gap-2 text-sm text-zinc-200"><span className="font-mono text-[#F3C649]">{i + 1}.</span>{typeof a === "string" ? a : a.text || a.title}</li>)}</ol>
        </div>
      </div>
    </Card>
  );
};
